import { useState } from "react";
import { ArrowRight, KeyRound } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, getApiError } from "@/lib/api";


export default function PasswordResetPage({ mode }) {
  const isRequest = mode === "request";
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");

    try {
      if (isRequest) {
        await api.post("/auth/forgot-password", { email });
      } else {
        await api.post("/auth/reset-password", { token, new_password: newPassword });
      }
      setDone(true);
    } catch (submitError) {
      setError(getApiError(submitError));
    } finally {
      setBusy(false);
    }
  };

  const copy = isRequest
    ? {
        title: "Wachtwoord vergeten",
        subtitle: "Vul je e-mailadres in. Als het bij ons bekend is, sturen we een herstellink.",
        cta: "Stuur herstellink",
        doneMessage: (
          <>
            Als dit e-mailadres bekend is, is er een herstellink verstuurd.
            <br />
            <br />
            Controleer je inbox. Kijk ook even in je spam- of ongewenste-mailmap indien je de e-mail niet onmiddellijk ziet.
          </>
        ),
      }
    : { title: "Nieuw wachtwoord", subtitle: "Kies een nieuw wachtwoord van minimaal 8 tekens.", cta: "Wachtwoord opslaan", doneMessage: "Wachtwoord bijgewerkt. Je kunt nu inloggen met je nieuwe wachtwoord." };

  return (
    <div
      className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(255,209,102,0.34),_transparent_30%),radial-gradient(circle_at_bottom,_rgba(111,168,132,0.22),_transparent_26%),linear-gradient(180deg,_#fbf7ef_0%,_#f4efe3_50%,_#edf3ea_100%)] px-4 sm:px-6"
      style={{
        minHeight: "100dvh",
        paddingTop: "max(1.5rem, env(safe-area-inset-top))",
        paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))",
      }}
    >
      <div className="mx-auto flex w-full max-w-md flex-col justify-between rounded-[34px] border border-white/80 bg-white/78 p-5 soft-shadow page-enter" style={{ minHeight: "calc(100dvh - max(3rem, env(safe-area-inset-top) + env(safe-area-inset-bottom)))" }}>
        <div>
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-[20px] bg-emerald-700 text-white" data-testid="reset-brand-badge">
            <KeyRound className="h-7 w-7" />
          </div>
          <p className="mt-5 text-xs font-semibold uppercase tracking-[0.32em] text-emerald-700" data-testid="reset-kicker">
            Smartly
          </p>
          <h1 className="mt-3 text-4xl font-semibold leading-tight text-stone-900" data-testid="reset-title">
            {copy.title}
          </h1>
          <p className="mt-3 text-sm text-stone-600" data-testid="reset-subtitle">
            {copy.subtitle}
          </p>

          {!isRequest && !token ? (
            <div className="mt-7 rounded-[22px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="reset-missing-token-message">
              Deze herstellink is onvolledig. Vraag een nieuwe link aan via{" "}
              <Link to="/forgot-password" className="font-medium underline" data-testid="reset-request-new-link">
                wachtwoord vergeten
              </Link>
              .
            </div>
          ) : done ? (
            <div className="mt-7 rounded-[22px] border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-800" data-testid="reset-success-message">
              {copy.doneMessage}
            </div>
          ) : (
            <form className="mt-7 space-y-4" onSubmit={handleSubmit} data-testid="reset-form">
              {isRequest ? (
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" htmlFor="reset-email">
                    E-mail
                  </label>
                  <Input id="reset-email" name="email" type="email" inputMode="email" autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} value={email} onChange={(event) => setEmail(event.target.value)} className="h-12 rounded-2xl border-stone-200 bg-white" data-testid="forgot-password-email-input" required />
                </div>
              ) : (
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" htmlFor="reset-new-password">
                    Nieuw wachtwoord
                  </label>
                  <Input id="reset-new-password" name="new-password" type="password" autoComplete="new-password" minLength={8} maxLength={72} value={newPassword} onChange={(event) => setNewPassword(event.target.value)} className="h-12 rounded-2xl border-stone-200 bg-white" data-testid="reset-password-input" required />
                </div>
              )}

              {error ? (
                <div className="rounded-[22px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="reset-error-message">
                  {error}
                </div>
              ) : null}

              <Button type="submit" className="h-12 w-full rounded-full bg-emerald-700 text-white hover:bg-emerald-800" disabled={busy} data-testid="reset-submit-button">
                {busy ? "Even bezig..." : copy.cta}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </form>
          )}
        </div>

        <div className="rounded-[24px] border border-stone-200 bg-white/80 px-4 py-4" data-testid="reset-footer-card">
          <Link to="/login" className="text-sm font-medium text-emerald-700" data-testid="reset-back-to-login-link">
            Terug naar inloggen
          </Link>
        </div>
      </div>
    </div>
  );
}
